import ReactionAndComment from 'components/reactionAndComments';
import { PlayerReactions } from 'components/reactionAndComments/PlayerReactions';
import { VideoPlayer } from 'components/videoPlayer';
import { useMainProviderContext } from 'context/main/provider';
import { wrapAnnotationWithProps } from 'queries/details/detailsQuery';
import { CSSProperties } from 'react';
import { AdditionalTextContainer, ReactionContainer } from 'style/Main';
import { CtaSetsButtons } from './CtaSetsButtons';
import { AdditionalText } from './AdditionalText';
import { PREVIEW_VIDEO_URL } from 'const/landingPageBuilderPreview';
import { cleanAdditionalText } from 'utils/functions';
import { Flex } from 'components/layout';
import { Map } from 'components/Map';

interface IProps {
  style?: CSSProperties;
  videoRef: any;
  areControlsShown: boolean;
  setAreControlsShown: ((arg: boolean) => void) | undefined;
  showContactInfoModal: boolean | undefined;
  openShowContactInfoModal: (() => void) | undefined;
}

// only on service flow
export const PlaybackDataVideoPlayer = ({
  style,
  videoRef,
  setAreControlsShown,
  areControlsShown,
  showContactInfoModal,
  openShowContactInfoModal,
}: IProps) => {
  const { isPreview, isBuilderPreviewMode, response, triggerViewNotification } =
    useMainProviderContext();
  const {
    interactiveCTAs = [],
    viewerStatisticId,
    annotations = [],
    companyAnnotations = [],
    video,
    videoId,
    playerDesign,
    hidePoweredBy,
    transcript,
  } = response || {};

  const filteredInteractiveCta = interactiveCTAs
    .filter(cta => cta.isVisible)
    .map(cta => ({
      label: cta.title,
      value: cta.url,
      type: cta.type,
      linkValue: cta.linkValue,
    }));

  const previewAnnotation = [
    ...annotations.map(a => wrapAnnotationWithProps(a)),
    ...companyAnnotations.map(a =>
      wrapAnnotationWithProps(a, { isCompanyAnnotation: true })
    ),
  ];

  const cleanedAdditionalText = cleanAdditionalText(
    response.landingPageDesign?.additionalText
  );

  const bodyOrientation = response.landingPageDesign.bodyOrientation;

  const justifyContentMap: Record<
    string,
    'center' | 'flex-start' | 'flex-end'
  > = {
    center: 'center',
    left: 'flex-start',
    right: 'flex-end',
  };

  return (
    <Flex
      justifyContent={justifyContentMap[bodyOrientation] ?? 'center'}
      flexDirection='row'
      alignItems='center'
      className='lp-container'
    >
      {/* Video Player Section */}
      <Flex maxWidth='800px' className='video-player-section'>
        {isBuilderPreviewMode ? (
          <VideoPlayer
            hidePoweredBy
            videoSource={PREVIEW_VIDEO_URL}
            videoRef={videoRef}
            playerBackgroundColor={playerDesign?.playerBackgroundColor}
            playerIconsColor={playerDesign?.playerIconsAndTextColor}
            playButtonPosition={playerDesign?.playButtonPosition}
            interactiveCTAs={filteredInteractiveCta}
            transcript={transcript}
          />
        ) : (
          <VideoPlayer
            width={style?.width ? `${style.width}px` : '800px'}
            height={style?.height ? `${style.height}px` : '450px'}
            preventWideSize
            videoSource={video?.videoSource}
            thumbnail={video?.thumbnail}
            videoRef={videoRef}
            interactiveCTAs={filteredInteractiveCta}
            videoId={videoId}
            userId={triggerViewNotification ? viewerStatisticId : 0}
            isPreview={isPreview}
            playerBackgroundColor={playerDesign?.playerBackgroundColor}
            playerIconsColor={playerDesign?.playerIconsAndTextColor}
            playButtonPosition={playerDesign?.playButtonPosition}
            isAutomotive
            previewAnnotation={previewAnnotation}
            setAreControlsShown={setAreControlsShown}
            videoChapters={video?.chapters || []}
            showContactInfoModal={showContactInfoModal}
            openShowContactInfoModal={openShowContactInfoModal}
            hidePoweredBy={hidePoweredBy}
            transcript={transcript}
          />
        )}
        {!isBuilderPreviewMode && (
          <ReactionContainer>
            <PlayerReactions
              hasControls={areControlsShown}
              videoDetails={video}
            />
          </ReactionContainer>
        )}

        {!isBuilderPreviewMode && (
          <ReactionAndComment videoRef={videoRef} playbackData={response} />
        )}

        {/* CTA and Additional Text Section  */}
        <Flex>
          <CtaSetsButtons />
          {cleanedAdditionalText && (
            <AdditionalTextContainer
              className='additional-text'
              withBackground
              additionalTextColor={
                response.landingPageDesign?.additionalTextColor || ''
              }
              additionalTextBackgroundColor={
                response.landingPageDesign.additionalTextBackgroundColor
              }
            >
              <AdditionalText playbackData={response} />
            </AdditionalTextContainer>
          )}
        </Flex>
        {/* CTA and Additional Text Section  */}
        {!isBuilderPreviewMode && !!response?.mapIframeEmbedCode && (
          <Flex>
            <Map
              landingPageDesign={response.landingPageDesign}
              mapIframeEmbedCode={response.mapIframeEmbedCode}
            />
          </Flex>
        )}
      </Flex>
      {/* Video Player Section */}
    </Flex>
  );
};
